import Modal from '@components/common/modal'
import { useState } from 'react'
import { FastlyImage } from '@components/common/image'
import CommonCarousel from '@components/common/carousel'
import BookFreeClassButton from '@components/common/book-free-class-btn'
import { SizeType } from '@components/atoms/button'
import { AcademicCap, LinkedIn } from '../common/icons/custom-icons'

export interface InstructorInfo {
  name: string
  cardSnippet: string
  cardLinkText?: string
  modalTitle: string
  degree?: string
  hookLine: string
  modalStory: string
  image: string
  cardColor?: string
  linkedinUrl?: string
}

const INSTRUCTORS: InstructorInfo[] = [
  {
    name: 'Aditya Vikram Singh',
    hookLine:
      'Built leadership through teaching at scale and supporting educators worldwide.',
    cardSnippet:
      'From classroom teaching to leadership, Aditya grew by guiding students and mentoring teachers across regions.',
    modalTitle: 'Growing as an educator and leader',
    modalStory:
      'My journey at Codingal shaped me as both an educator and a leader. Teaching students across grades and countries strengthened my technical skills and communication. Mentoring other teachers added a new sense of responsibility. One student who barely spoke at the start slowly began participating and helping others. That change stayed with me. It reminded me that teaching is also about building confidence. Codingal helped me grow steadily and create impact beyond individual lessons.',
    image: 'aditya.png',
    cardColor: 'blue-100',
    linkedinUrl: 'https://www.linkedin.com/in/aditya-vikram-singh-712063164/',
  },
  {
    name: 'Nabeela Akram',
    hookLine:
      'Turned academic knowledge into confident, practical teaching that empowers learners.',
    cardSnippet:
      'Built confidence through hands-on teaching and helped students believe they can do hard things.',
    modalTitle: 'Confidence built through teaching',
    modalStory:
      'I understood coding academically, but teaching it with confidence was a different challenge. Codingal helped bridge that gap. With every class, my clarity improved and my confidence grew. I learned how to make concepts practical and relatable. The best part is seeing students move from hesitation to independence. Codingal’s supportive environment helped me grow at my own pace while staying focused on student learning. Teaching here made me a stronger, calmer, and more confident educator.',
    image: 'nabeela.png',
    cardColor: 'violet-100',
    linkedinUrl: 'https://www.linkedin.com/in/nabeela-a-18931b15a/',
  },
  {
    name: 'Swathi K',
    hookLine:
      'Found her calling in education and grew into leadership through global classrooms.',
    cardSnippet:
      'Grew alongside students and stepped into leadership while teaching learners across countries.',
    modalTitle: 'Learning, teaching, and growing together',
    modalStory:
      'Teaching at Codingal has been a journey of shared growth. Students bring curiosity and excitement into every class, and that energy motivates me to do better. Codingal helped me strengthen my understanding of advanced concepts and gave me global exposure. Over time, I learned how to guide students patiently and celebrate real progress. A leadership role came later, but the real reward is still the classroom moments. When a student applies a new concept with confidence, it reminds me why I chose education.',
    image: 'swathi.png',
    cardColor: 'yellow-200',
  },
  {
    name: 'Laiba Rahat',
    hookLine:
      'Through Apollo, she went beyond regular teaching with stronger confidence, clarity, and earnings.',
    cardSnippet:
      'Apollo helped her grow as a Teacherpreneur—building ownership, stability, and confidence beyond classes.',
    modalTitle: 'Growing beyond teaching through Apollo',
    modalStory:
      'Teaching at Codingal helped me grow as an educator. The Apollo Program helped me grow as a professional. Apollo is a Teacherpreneur opportunity that goes beyond regular teaching. It helped me take ownership of outcomes, build confidence, and gain clarity about my career. I also had the opportunity to earn beyond my regular teaching income, which brought more stability and independence. What I value most is that it still stays rooted in quality teaching. I could grow professionally while continuing to focus on meaningful student learning.',
    image: 'laiba.png',
    cardColor: 'red-200',
    linkedinUrl: 'https://www.linkedin.com/in/laiba-rahat7/',
  },
  {
    name: 'Minakshi Daga',
    hookLine:
      'Teaches students across India, UAE, Europe, and Southeast Asia with confidence and clarity.',
    cardSnippet:
      'Global classrooms helped her become more adaptable, confident, and student-first in every lesson.',
    modalTitle: 'Learning through diverse classrooms',
    modalStory:
      'Teaching students from different countries and age groups changed how I approach education. Each learner brought a new perspective. Codingal helped me become more adaptable, patient, and confident. My leadership and communication improved naturally through teaching. The most fulfilling moments are when students move from confusion to clarity and begin solving problems independently. Those moments remind me that teaching shapes how students see themselves.',
    image: 'minakshi.png',
    cardColor: 'orange2-400',
    linkedinUrl: 'https://www.linkedin.com/in/minakshi-daga/',
  },
  {
    name: 'Amit Chander Dhanwani',
    hookLine:
      'Built lasting impact through trust, student milestones, and proud breakthrough moments.',
    cardSnippet:
      'Meaningful feedback and student growth define his teaching journey at Codingal.',
    modalTitle: 'Moments that define teaching',
    modalStory:
      'Some moments in teaching stay with you forever. For me, it has been watching students grow from beginners into confident creators. It’s also been the trust families place in my teaching. When students build projects they are proud of, or debug something on their own for the first time, it feels deeply meaningful. Codingal allowed me to keep learning and bring that growth into my classes. These moments remind me that teaching is about confidence as much as it is about code.',
    image: 'amit.png',
    cardColor: 'orange-600',
    linkedinUrl: 'https://www.linkedin.com/in/amit-dhanwani-aa173559/',
  },
  {
    name: 'Moksha Sanghavi',
    hookLine:
      'Built a stable remote career while supporting her parents and growing as an educator.',
    cardSnippet:
      'Remote teaching gave her stability, purpose, and the joy of seeing students grow week by week.',
    modalTitle: 'Stability, purpose, and student growth',
    modalStory:
      'Teaching at Codingal gave me stability and flexibility at the same time. Working remotely helped me support my parents while continuing to grow professionally. Over time, I became more confident and more consistent in how I teach. What matters most is the bond I share with my students. Seeing them improve, ask questions, and feel proud of their work brings real satisfaction. Codingal helped me build a career that feels dependable and meaningful.',
    image: 'moksha.png',
    cardColor: 'green-100',
    linkedinUrl: 'https://www.linkedin.com/in/moksha-sanghavi-1722aa148/',
  },
  // {
  //   name: 'Mayuri Rastogi',
  //   hookLine:
  //     'Grew from trainee to technical interviewer through learning, resilience, and strong support.',
  //   cardSnippet:
  //     'A journey shaped by continuous growth and a team that showed up when it mattered.',
  //   modalTitle: 'A journey shaped by learning and support',
  //   modalStory:
  //     'I joined Codingal as a trainee and gradually grew into multiple roles. Along the way, I learned new technologies and strengthened my teaching. What made this journey special was the support I received during challenging times. Being selected for quality evaluation was a turning point. From there, I took on responsibilities as a mentor and technical interviewer. Codingal gave me space to grow steadily, build confidence, and take pride in the work I do.',
  //   image: 'mayuri.png',
  //   cardColor: 'grey-700',
  // },
  {
    name: 'Paras Bajaj',
    hookLine:
      'Strengthened skills over time and helped students gain confidence through real projects.',
    cardSnippet:
      'A long-term journey of learning, student impact, and steady professional growth.',
    modalTitle: 'Growing with every milestone',
    modalStory:
      'My time at Codingal has been about steady improvement. I learned new skills, explored advanced concepts, and became more confident in the classroom. Teaching students across regions helped me grow as an educator. Supporting learners as they build projects and prepare for challenges has been especially fulfilling. Codingal played an important role in shaping my teaching journey and my personal growth.',
    image: 'paras.png',
    cardColor: 'blue-100',
    linkedinUrl: 'https://www.linkedin.com/in/paras-bajaj-a9a367227/',
  },
  {
    name: 'Ayesha Mohammad',
    hookLine:
      'Grew into a confident educator by helping students overcome fear and enjoy learning.',
    cardSnippet:
      'A journey of confidence, creativity, and student breakthroughs—one class at a time.',
    modalTitle: 'Finding confidence through teaching',
    modalStory:
      'When I joined Codingal, I felt excited but unsure. Over time, teaching here helped me grow into a more confident educator. I learned new ways to engage students and encourage creativity. The most rewarding moments are when students overcome hesitation and begin enjoying learning on their own. Codingal created an environment where growth felt supported and natural. Teaching here changed how I see my own abilities.',
    image: 'ayesha.png',
    cardColor: 'violet-100',
  },
  {
    name: 'Amith Singh',
    hookLine:
      'Turned an internship into a meaningful career—now leading text-based curriculum and training hundreds of teachers.',
    cardSnippet:
      'From developer intern to senior curriculum leader, shaping how students learn text-based coding at scale.',
    modalTitle: 'From internship to curriculum leadership',
    modalStory:
      'I joined Codingal as an intern with a developer background. Over time, I became deeply involved in curriculum creation. What began as writing and building slowly expanded into designing how students learn text-based programming. Today, I help shape advanced coding courses and train hundreds of teachers. Supporting educators with clarity and confidence has been one of the most meaningful parts of my journey. Codingal gave me room to grow step by step and turn an internship into a career with purpose.',

    image: 'amith.png',
    cardColor: 'yellow-200',
    linkedinUrl: 'https://www.linkedin.com/in/amith-singh-825930205/',
  },
  {
    name: 'Freeda Felix Kakara',
    hookLine:
      'Started as a teacher and grew into curriculum design—creating joyful block-based learning for young students.',
    cardSnippet:
      'From teaching to curriculum building, she designs lessons that feel simple, creative, and confidence-building.',
    modalTitle: 'From classroom to curriculum',
    modalStory:
      'My journey at Codingal began in the classroom. Teaching young learners helped me understand how children think and learn. Over time, I was given the opportunity to grow into curriculum development. Today, I design block-based coding lessons that are engaging, easy to follow, and confidence-building. I enjoy making learning feel joyful and approachable. As a woman educator, I value being in a space that supports growth and leadership. Codingal helped me turn my love for teaching into a wider impact.',

    image: 'freeda.png',
    cardColor: 'red-200',
    linkedinUrl: 'https://www.linkedin.com/in/freeda-felix-kakara/',
  },
]

export const MATH_INSTRUCTORS: InstructorInfo[] = [
  {
    name: 'Aditi Aggarwal',
    modalTitle: 'Team Lead & Mentor',
    degree: 'Certified Chartered Accountant & Economics Hons.',
    cardSnippet:
      'A quiet student who once avoided math classes now walks in excited to solve problems and share answers confidently.',
    cardLinkText: "Read how she rebuilt a student's confidence →",
    hookLine: 'Turning quiet participation into confident learning',
    modalStory:
      'I once taught a Grade 4 student who was not very expressive and needed a different way of learning. In the first few classes, we started with fractions, but I could see that the regular lesson flow felt overwhelming for him.\nSo instead of simply moving ahead with the PPT, I changed my approach. I made the classes more interactive and quiz-based because that was the way he responded best. Slowly, he began engaging more. What started as quiet participation turned into curiosity, confidence, and excitement.\nToday, he does not like missing his math classes. From barely speaking in the beginning, he now comes to class with little math tricks to show me. In one of our recent classes, he proudly showed me the criss-cross trick for multiplying two-digit numbers.\nSeeing that excitement in every class is deeply rewarding. After a tiring day, moments like these remind me why teaching math is so special.\nFor me, math gives every child a new way to look at the world. It is present everywhere, in shapes, numbers, patterns, coordinate grids, and even advanced ideas like trigonometry. But beyond marks and grades, math builds logical reasoning, problem-solving skills, and confidence.',
    image: 'aditi-aggarwal.png',
    cardColor: 'blue-100',
    linkedinUrl: 'https://www.linkedin.com/in/aditi-aggarwal-teacher',
  },
  {
    name: 'Kavya S',
    modalTitle: 'Senior Math Instructor',
    degree: 'M.Tech',
    cardSnippet:
      'One student went from waiting silently for help to solving challenging problems independently, step by step.',
    cardLinkText: 'See how she made math feel approachable →',
    hookLine: 'Helping a child feel that math can be easy',
    modalStory:
      'One of my students initially found fractions very challenging and was hesitant to participate during class. The topic felt difficult, and the student was not confident about trying problems independently.\nSo I started using simple visual methods and examples that felt more relatable. Instead of rushing through the concept, we went step by step, in a way that helped the student understand what fractions actually mean.\nOver the next few weeks, I could see a clear change. The student began participating more, solving problems with less hesitation, and gradually building confidence in the topic.\nThen one day, the student solved the problems independently and said, "Math feels easy now."\nThat moment was very special for me. It reminded me how powerful personalized learning can be when a child is given the right support and enough time to grow.\nI believe math gives children much more than academic success. It builds confidence, logical thinking, patience, and problem-solving abilities.',
    image: 'kavya-s.png',
    cardColor: 'violet-100',
    linkedinUrl: 'https://www.linkedin.com/in/kavya-s-a71564159',
  },
  {
    name: 'Shivangi Pandey',
    modalTitle: 'Math & Coding Instructor',
    degree: 'B.Tech in Computer Science and Technology',
    cardSnippet:
      'After losing confidence in math, a student slowly rediscovered curiosity, consistency, and the courage to try again.',
    cardLinkText: 'Read how small wins changed her learning →',
    hookLine: 'From "Math is not for me" to asking for more questions',
    modalStory:
      'One of my most memorable teaching moments was with a Grade 3 student who had completely lost interest in math. His parents were very concerned because he was struggling with basic concepts and not performing well in school.\nIn the first few lessons, I noticed the issue was not his ability but his confidence. He had missed some foundational concepts and started believing that math was not for him.\nAt that point, he was not comfortable solving even basic 2-digit multiplication problems independently. So instead of rushing ahead, I focused on rebuilding his basics step by step. I made lessons interactive, encouraging, and achievable. We celebrated small wins and connected concepts to patterns and logic instead of memorization.\nAfter just 5 to 6 lessons, the transformation was incredible. He started understanding the concepts he had missed earlier, and with every class, his confidence grew. The same child who once avoided math problems began asking for extra questions.\nSoon, he was confidently solving 3-digit and even 4-digit multiplication problems, learning decimals, and doing far more than expected in such a short time.\nOne day, his parents called me and said, "What magic did you do? He comes to us after every lesson excited to teach us what he learned."\nThat moment meant a lot because he had not only improved academically, he had started enjoying math.',
    image: 'shivangi-pandey.png',
    cardColor: 'yellow-200',
    linkedinUrl: 'https://www.linkedin.com/in/shivangi-pandey-86b4011aa',
  },
  {
    name: 'J Rahul',
    modalTitle: 'Senior Math Instructor',
    degree: 'B.Tech and MBA',
    cardSnippet:
      'A quiet student who once struggled with problem-solving slowly gained confidence after finally understanding decimals step by step.',
    cardLinkText: 'See how confidence grew over time →',
    hookLine: 'Making fractions feel real, visual, and fun',
    modalStory:
      'I have been teaching a student named Nathania for the last 10 months. When she first joined, she was very reserved and rarely opened up during sessions. She also struggled with problem-solving, and I was unsure in the beginning how much progress we would be able to make.\nAfter a few classes, we started working on decimal conversions. She found it difficult at first and was hesitant while solving questions. So we went step by step and revisited the concept patiently until she slowly became more comfortable.\nThen came a moment I still remember clearly. After a few attempts, she finally understood the concept, and the smile and relief on her face said it all. That moment was not just about decimals, it felt like she had started opening up and trusting the learning process.\nSince then, classes have become smoother. She is more confident, more expressive, and much more willing to attempt problems on her own. A lot of credit also goes to her for trusting the process and continuing to believe in herself.\nFor me, teaching math is not just about academics. It is about helping students feel safe making mistakes, building confidence step by step, and realizing they are capable of learning difficult things with patience and support.',
    image: 'j-rahul.png',
    cardColor: 'orange2-400',
  },
  {
    name: 'Shivangi Sharma',
    modalTitle: 'Senior Coding and Math Instructor',
    degree: 'B.Tech in ECE and M.Tech in VLSI Design',
    cardSnippet:
      'Using pizzas, chocolates, and real-life examples, she helped a student finally understand fractions without fear.',
    cardLinkText: 'See how fear turned into confidence →',
    hookLine: 'Making fractions feel real, visual, and fun',
    modalStory:
      'I remember teaching a student who was very scared of fractions and often said that math was difficult. The topic felt confusing at first and the student was not confident about solving questions independently.\nSo I started explaining fractions with simple, everyday examples like pizza slices and chocolates. I wanted the concept to feel real, visual, and fun instead of something to be feared.\nSlowly, the student began connecting with the idea. Fractions were no longer just numbers on a page. They became something the student could see, understand, and relate to.\nOne day, the student solved a fractions question without any help and happily said, "Ma\'am, now I understood it!"\nThat moment made me feel very proud because I could see the confidence growing in the child.\nI believe math is not only about marks and grades. It teaches children patience, confidence, logical thinking, and problem-solving skills.',
    image: 'shivangi-sharma.png',
    cardColor: 'green-100',
    linkedinUrl: 'https://www.linkedin.com/in/shivangi-sharma-943207178',
  },
]

const AboutTeachers = ({
  urlQuery,
  ctaSize,
  title,
  description,
  subject,
}: {
  urlQuery?: string
  ctaSize?: SizeType
  title?: string
  description?: string
  subject?: 'coding' | 'math'
}) => {
  const activeInstructors = subject === 'math' ? MATH_INSTRUCTORS : INSTRUCTORS
  const [showTeacherInfoModal, setShowTeacherInfoModal] = useState<boolean>()
  const [selectedTeacher, setSelectedTeacher] = useState<InstructorInfo>()

  const TeacherModal = () => (
    <Modal
      isOpen={showTeacherInfoModal}
      setIsOpen={setShowTeacherInfoModal}
      width="lg:container"
      shouldCloseOnEsc={true}
      isCrossIconShown
      className="z-50 h-128 lg:h-auto"
    >
      <div className="flex flex-col">
        <div className="flex flex-col lg:flex-row lg:divide-x border-grey-400">
          <div className="flex flex-col items-center lg:max-w-xs w-full pb-4 lg:pb-0 lg:pr-4">
            <FastlyImage
              src={`/images/about-us/teachers/${selectedTeacher.image}`}
              height={144}
              width={144}
              alt={selectedTeacher.name}
              className="hidden lg:block"
            />
            <h3 className="text-grey-900 font-700 text-2xl mt-6 text-center">
              {selectedTeacher.name}
            </h3>

            <p className="text-grey text-xl font-500 lg:font-400 text-center">
              {selectedTeacher.modalTitle}
            </p>
            {selectedTeacher.degree && (
              <div className="flex items-start gap-2 mt-2">
                <AcademicCap className="w-6 h-6 text-blue-200" />
                <p className="text-grey text-sm text-center lg:text-left">
                  {selectedTeacher.degree}
                </p>
              </div>
            )}
            {selectedTeacher.linkedinUrl && (
              <a
                href={selectedTeacher.linkedinUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-blue-300 hover:opacity-75 mt-1 focus:outline-none"
              >
                <LinkedIn className="w-5 h-5" />
                <span className="text-sm font-500">LinkedIn</span>
              </a>
            )}
          </div>
          <div className="text-center lg:pl-4 lg:text-left border-grey-400 max-w-3xl">
            <h3 className="text-grey-900 font-600 text-xl lg:text-2xl lg:mb-6">
              {selectedTeacher.hookLine}
            </h3>
            <p className="text-grey text-xl my-2">
              {selectedTeacher.modalStory}
            </p>
          </div>
        </div>
      </div>
    </Modal>
  )
  return (
    <div className="container mx-auto px-5 py-12 lg:py-16 flex flex-col space-y-10 leading-tight text-grey">
      <div className="flex flex-col">
        <h2 className="max-w-4xl mx-auto pb-4 text-2xl text-center text-grey lg:text-4xl font-600">
          {title ?? 'Hear from our change makers'}
        </h2>
        {description && (
          <p className="max-w-4xl mx-auto text-center text-grey lg:text-lg">
            {description}
          </p>
        )}
      </div>
      <div>
        <CommonCarousel hasArrows>
          {activeInstructors.map((e, index) => (
            <div
              key={index}
              className="slider flex justify-center mb-10 cursor-pointer"
              onClick={() => {
                setSelectedTeacher(e)
                setShowTeacherInfoModal(true)
              }}
            >
              <div
                className={`flex flex-col items-center gap-1 bg-opacity-50 rounded-lg shadow-md h-96 p-2 py-4 bg-${e.cardColor} transition-all duration-300 hover:shadow-xl`}
                style={{ width: '270px' }}
              >
                <div>
                  <FastlyImage
                    src={`/images/about-us/teachers/${e.image}`}
                    height={144}
                    width={144}
                    alt={e.name}
                  />
                </div>
                <h3 className="text-grey-900 font-700 text-lg mt-6 text-center uppercase">
                  {e.name}
                </h3>
                <p className="text-grey text-center flex-1">{e.cardSnippet}</p>
                <button
                  className="font-600 cursor-pointer focus:outline-none mt-4 text-sm sm:text-base transition-all duration-300 text-blue-300 hover:opacity-75 underline text-center"
                  onClick={() => {
                    setSelectedTeacher(e)
                    setShowTeacherInfoModal(true)
                  }}
                >
                  {e.cardLinkText ?? 'Read more →'}
                </button>
              </div>
            </div>
          ))}
        </CommonCarousel>
      </div>
      {urlQuery && (
        <div className="flex flex-col lg:flex-row gap-4 mt-12 justify-center mx-auto">
          <BookFreeClassButton size={ctaSize} query={urlQuery} />
        </div>
      )}
      {selectedTeacher && <TeacherModal />}
    </div>
  )
}

export default AboutTeachers
